import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Zap } from "lucide-react";
import { useLang } from "../lib/i18n";
import { useLenis } from "../components/motion/Reveal";
import { BlogHeader } from "../components/site/BlogHeader";
import { Footer } from "../components/site/Footer";
import { api } from "../lib/api";

export default function BlogPost() {
  useLenis();
  const { slug } = useParams();
  const { t, lang } = useLang();
  const nav = useNavigate();
  const [post, setPost] = useState(null);
  const [more, setMore] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/blog/${slug}?lang=${lang}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    api.get(`/blog?lang=${lang}`).then((r) => setMore(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="grain bg-charcoal min-h-screen">
        <BlogHeader backLabel={t.blog.all} />
        <div className="max-w-3xl mx-auto px-5 py-32 text-center">
          <p className="font-display text-3xl font-bold">404</p>
          <Link to="/blog" className="mt-4 inline-block text-lime2" data-testid="notfound-back">{t.blog.back}</Link>
        </div>
      </div>
    );
  }

  if (!post) return <div className="bg-charcoal min-h-screen" />;

  return (
    <div className="grain relative bg-charcoal min-h-screen overflow-x-hidden">
      <BlogHeader backLabel={t.blog.all} />

      <article className="max-w-3xl mx-auto px-5 md:px-8 pt-14 md:pt-20 pb-10" data-testid="blog-article">
        <button onClick={() => nav("/blog")} className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-lime2 transition-colors mb-8" data-testid="post-back">
          <ArrowLeft size={15} /> {t.blog.back}
        </button>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="text-[10px] uppercase tracking-[0.2em] text-lime2 border border-lime2/40 rounded-full px-3 py-1">
            {post.category}
          </span>
          <h1 className="font-display font-extrabold tracking-tighter text-4xl md:text-6xl leading-[0.95] mt-6">
            {post.title}
          </h1>
          <div className="mt-5 flex items-center gap-3 text-sm text-white/40">
            <span>{post.author}</span><span>·</span>
            <span>{post.read_time} {t.blog.readTime}</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 rounded-2xl overflow-hidden border border-white/10 aspect-[16/9]"
        >
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        </motion.div>

        <div className="mt-10 prose-invert max-w-none">
          {post.content.split("\n\n").map((para, i) => (
            <motion.p
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-white/75 text-lg leading-relaxed mb-6"
            >
              {para}
            </motion.p>
          ))}
        </div>

        <div className="mt-12 rounded-2xl border border-emerald2/40 bg-gradient-to-br from-emerald2/20 to-transparent p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <p className="font-display text-2xl font-bold">{t.footer.tagline}</p>
          <Link to="/#contact" className="inline-flex items-center gap-2 bg-emerald2 hover:bg-lime2 hover:text-black text-white font-semibold rounded-full px-6 py-3 transition-colors" data-testid="post-cta">
            <Zap size={16} /> {t.nav.contact}
          </Link>
        </div>
      </article>

      {more.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20">
          <h2 className="font-display text-2xl font-bold mb-6">{t.blog.more}</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {more.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} className="group block rounded-xl overflow-hidden border border-white/10 bg-[#181818]" data-testid={`more-${p.slug}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <h3 className="font-display font-bold group-hover:text-lime2 transition-colors">{p.title}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
